import React, { useEffect, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getDefaultOption } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { getAllComponents } from "utils/enclosure/components";
import { standardCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import expFloorCodeTemplate from "features/Model/Enclosure/Templates/expFloorCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import MultiSelect from "components/Input/MultiSelect";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";

import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";
import EmbodiedCarbonFramingGroupGrade, { EmbodiedCarbonCalculationsSummary, StandardEmbodiedLayerBlock } from "components/EmbodiedCarbonGrade";
import globalStyles from "components/globalStyles.module.scss";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const interiorValidation = getValidation("csExpFloorInterior");
const structureValidation = getValidation("csExpFloorStructureType");
const typeSizeValidation = getValidation("csExpFloorComponentTypeSize");
const sheathingValidation = getValidation("csExpFloorSheathing");
const framingSpacingValidation = getValidation("csExpFloorFramingSpacing");
const exteriorValidation = getValidation("csExpFloorExterior");
const ins1Validation = getValidation("csExpFloorInsLayer1");
const ins2Validation = getValidation("csExpFloorInsLayer2");
const dropFramingValidation = getValidation("csExpFloorDropFraming");

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const StandardFloorCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { expFloorInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } = useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } = useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const expFloorCode =
            useStateSelector(`${drawerAccessor}.expFloorCode`) || {};
        const { id: expFloorStructureId, value: expFloorStructureValue = 0 } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.structureType`) || {};
        const { id: expFloorSpacingId, value: expFloorSpacingValue } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.spacing`) || {};
        const { id: expFloorDropFramingId, value: expFloorDropFramingValue } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.dropFraming`) || {};
        const { value: expFloorTypeSizeValue } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.componentTypeSize`) || {};
        const { value: expFloorFramingValue } = useStateSelector(`${drawerAccessor}.expFloorCode.layers.framing`) || {};
        const { value: expFloorIns1Value } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.insulationLayer1`) || {};
        const { value: expFloorIns2Value } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.insulationLayer2`) || {};
        const { value: expFloorInteriorValue } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.interior`) || {};
        const { value: expFloorSheathingValue } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.sheathing`) || {};
        const { value: expFloorExteriorValue } =
            useStateSelector(`${drawerAccessor}.expFloorCode.layers.exterior`) || {};
        const expFloorLabel = useStateSelector(`${drawerAccessor}.expFloorCode.label`) || "";
        const expFloorLayers = useStateSelector(`${drawerAccessor}.expFloorCode.layers`) || {};
        const layersFieldPrefix = `${drawerAccessor}.expFloorCode.layers`;

        const frameSpacingValue = expFloorSpacingId === null ? expFloorFramingValue : expFloorSpacingValue;
        const expFloorValue = `3${expFloorStructureValue
            .toString()
            .substr(
                -1
            )}${expFloorTypeSizeValue}${frameSpacingValue}${expFloorIns1Value}${expFloorIns2Value}${expFloorInteriorValue}${expFloorSheathingValue}${expFloorExteriorValue}${
            expFloorDropFramingValue || 0
        }`;

        const isSolid = parseFloat(expFloorStructureId) === 4;
        const isPanel = parseFloat(expFloorStructureId) === 5;
        const isDropFraming = parseFloat(expFloorDropFramingId) === 1;

        const makeSpacingDefault = ["5", "6", "7", "8", "9", "A", "B", "C"].includes(expFloorTypeSizeValue);
        const makeInsulationDefault = ["6", "7", "8", "9", "A", "B", "C"].includes(expFloorTypeSizeValue);
        const makeDropFramingInsDefault = ["9", "A", "E", "F", "U", "M", "N", "O", "P", "Q", "R", "S", "T"].includes(
            expFloorIns1Value
        );

        const { codeRef: editingCodeRef = null, isLibCode = false } = expFloorInsType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("Floor", fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"]) ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents) : selectedComponents;

        const valueRef = useRef(null);

        const handleStuctureChange = useCallback(
            ({ id, value }) => {
                change(
                    `${drawerAccessor}.expFloorCode.layers.insulationLayer1`,
                    getFirstOption({ indKey: id, depKey: "csExpFloorInsLayer1" })
                );
                change(
                    `${drawerAccessor}.expFloorCode.layers.componentTypeSize`,
                    getFirstOption({ indKey: id, depKey: "csExpFloorComponentTypeSize" })
                );

                // Use "Framing" when set to Solid, use Spacing otherwise
                if (parseFloat(id) === 4) {
                    //set spacing to null and grab first option for framing
                    change(`${drawerAccessor}.expFloorCode.layers.spacing`, { id: null });
                    change(
                        `${drawerAccessor}.expFloorCode.layers.framing`,
                        getFirstOption({
                            indKey: id,
                            depKey: "csExpFloorFramingSpacing",
                        })
                    );
                } else {
                    //set framing to null and grab first option for spacing
                    change(`${drawerAccessor}.expFloorCode.layers.framing`, { id: null });

                    // Only grab default spacing id if not set
                    if (expFloorSpacingId === null) {
                        change(
                            `${drawerAccessor}.expFloorCode.layers.spacing`,
                            getDefaultOption("csExpFloorFramingSpacing")
                        );
                    }
                }

                // Panel
                if (parseFloat(id) === 5) {
                    change(
                        `${drawerAccessor}.expFloorCode.layers.insulationLayer1`,
                        getDefaultOption("csExpFloorInsLayer1")
                    );
                    change(
                        `${drawerAccessor}.expFloorCode.layers.insulationLayer2`,
                        getDefaultOption("csExpFloorInsLayer2")
                    );
                    change(
                        `${drawerAccessor}.expFloorCode.layers.spacing`,
                        getDefaultOption("csExpFloorFramingSpacing")
                    );
                    change(
                        `${drawerAccessor}.expFloorCode.layers.dropFraming`,
                        getDefaultOption("csExpFloorDropFraming")
                    );
                }
            },
            [expFloorStructureId, change, expFloorSpacingId]
        );

        useEffect(() => {
            const initialValues = {
                expFloorCode: isEmpty(expFloorInsType) ? expFloorCodeTemplate : expFloorInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = expFloorValue;
        }, [expFloorValue, expFloorLabel]);

        //Filter Insulation Layer 1 for drop framing
        const baseInsLayer1Opts = [
            "0",
            "K",
            "1",
            "2",
            "X",
            "Y",
            "3",
            "4",
            "Z",
            "a",
            "5",
            "L",
            "c",
            "J",
            "b",
            "d",
            "V",
            "W",
            "I",
            "e",
        ];
        let insLayer1Opts = getOptions({ fieldKey: "csExpFloorInsLayer1", indKey: expFloorStructureId });
        if (isDropFraming) {
            insLayer1Opts = insLayer1Opts.filter((opt) => baseInsLayer1Opts.includes(opt.value.value));
        }

        let availableComponents = getAllComponents("expFloor", components, fieldAccessor).sort(codeSort);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        const expFloorEmbRowStyle = {
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            marginBottom: "4px",
        };

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect || (!isCodeLibrary && !isUpgradeComponent) ? "1fr 1fr 1fr" : "1fr 1fr"}  style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.expFloorCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={expFloorLabel.toString() === valueRef.current ? expFloorValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.expFloorCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={expFloorValue}
                    />
                    {!isCodeLibrary && !isUpgradeComponent && (
                        <Field
                            component={MultiSelect}
                            name={`${drawerAccessor}.selectedComponents`}
                            label={`Apply to multiple components`}
                            options={availableComponents.map(({ disabled = false, label = "", ...rest }) => ({
                                ...rest,
                                disabled,
                                label: disabled ? `${label} (current component)` : label,
                            }))}
                            hasSelectAll
                        />
                    )}
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <Field
                                    component={Checkbox}
                                    name={`${drawerAccessor}.addToLibrary`}
                                    label={isLibCode ? "Update code in library" : "Add to code library"}
                                    large
                                    disabled={isLibCode}
                                    type="checkbox"
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                            />
                        </>
                    )}
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.expFloorCode.layers.structureType`}
                            label="Structure Type"
                            placeholder="Choose Structure Type"
                            options={getOptions({ fieldKey: "csExpFloorStructureType" })}
                            validate={structureValidation}
                            onChange={handleStuctureChange}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.expFloorCode.layers.componentTypeSize`}
                            label="Component Type / Size"
                            placeholder="Choose Type / Size"
                            options={getOptions({
                                fieldKey: "csExpFloorComponentTypeSize",
                                indKey: expFloorStructureId,
                            })}
                            validate={typeSizeValidation}
                            onChange={({ value = "" }) => {
                                if (["6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(
                                        `${drawerAccessor}.expFloorCode.layers.insulationLayer1`,
                                        getDefaultOption("csExpFloorInsLayer1")
                                    );
                                    change(
                                        `${drawerAccessor}.expFloorCode.layers.insulationLayer2`,
                                        getDefaultOption("csExpFloorInsLayer2")
                                    );
                                }
                                if (["5", "6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(`${drawerAccessor}.expFloorCode.layers.framing`, { id: 0, value: "0" });
                                }
                            }}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        {isSolid ? (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.expFloorCode.layers.framing`}
                                label="Framing"
                                placeholder="Choose Framing"
                                options={getOptions({
                                    fieldKey: "csExpFloorFramingSpacing",
                                    indKey: expFloorStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={makeSpacingDefault}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        ) : (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.expFloorCode.layers.spacing`}
                                label="Spacing"
                                placeholder="Choose Spacing"
                                options={getOptions({
                                    fieldKey: "csExpFloorFramingSpacing",
                                    indKey: expFloorStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={isPanel}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        )}
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.expFloorCode.layers.dropFraming`}
                            label="Drop Framing"
                            placeholder="Choose"
                            options={getOptions({ fieldKey: "csExpFloorDropFraming" })}
                            validate={dropFramingValidation}
                            disabled={isPanel}
                            onChange={({ value = "" }) => {
                                if (makeDropFramingInsDefault && value == 1) {
                                    change(
                                        `${drawerAccessor}.expFloorCode.layers.insulationLayer1`,
                                        getDefaultOption("csExpFloorInsLayer1")
                                    );
                                }
                            }}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                    <EmbodiedCarbonFramingGroupGrade
                        layers={expFloorLayers}
                        componentType="expFloor"
                        customPadding="4px 0 0 0"
                        change={change}
                        layersFieldPrefix={layersFieldPrefix}
                        customGap={"1rem"}
                        barMetric={barMetric}
                    />
                </div>

                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer1"
                    layers={expFloorLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="expFloor"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={expFloorEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.expFloorCode.layers.insulationLayer1`}
                        label="Framing Cavity Insulation (Layer 1)"
                        placeholder="Choose Insulation"
                        options={insLayer1Opts}
                        validate={ins1Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer2"
                    layers={expFloorLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="expFloor"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={expFloorEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.expFloorCode.layers.insulationLayer2`}
                        label="Continuous Layer of Insulation (Layer 2)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csExpFloorInsLayer2" })}
                        validate={ins2Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <StandardEmbodiedLayerBlock
                    layerType="interior"
                    layers={expFloorLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="expFloor"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={expFloorEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.expFloorCode.layers.interior`}
                        label="Interior"
                        placeholder="Choose Interior"
                        options={getOptions({ fieldKey: "csExpFloorInterior" })}
                        validate={interiorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="sheathing"
                    layers={expFloorLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="expFloor"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={expFloorEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.expFloorCode.layers.sheathing`}
                        label="Sheathing"
                        placeholder="Choose Sheathing"
                        options={getOptions({ fieldKey: "csExpFloorSheathing" })}
                        validate={sheathingValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="exterior"
                    layers={expFloorLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="expFloor"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={{ ...expFloorEmbRowStyle, marginBottom: 0 }}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.expFloorCode.layers.exterior`}
                        label="Exterior"
                        placeholder="Choose Exterior"
                        options={getOptions({ fieldKey: "csExpFloorExterior" })}
                        validate={exteriorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={expFloorCode}
                    componentType={"expFloor"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.expFloorCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "expFloorInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            expFloorCode = {},
            addToLibrary = false,
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        standardCodeLogic({
            change,
            componentCodeType: "Floor",
            componentId,
            code: expFloorCode,
            inputName: "expFloorInsType",
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardFloorCode);
